import * as d3 from 'd3';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as uuid from 'uuid';

import {
  Component,
  Inject,
  AfterViewInit,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { SnCamera } from '@lib/api/entities';
import { SnPlayerState } from '@lib/player/common';
import { SnDialogService } from '@lib/ui/dialog';
import { BASE_COLORS } from '@lib/ui/theme';

export class Shape {
  public id: string;
  public points: any[];
  public gridPoints: any[];

  constructor() {
    this.points = [];
    this.gridPoints = [];
  }
}

@Component({
  selector: 'sn-camera-ai-grid-dialog',
  templateUrl: './camera-ai-grid-dialog.component.html',
  styleUrls: ['./camera-ai-grid-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraAiGridDialogComponent implements AfterViewInit, OnDestroy {
  public isPolygonComplete: boolean = false;
  public isLoading: boolean = false;
  public resolutionX: number;
  public resolutionY: number;
  public gridX: number;
  public gridY: number;
  public drawing: Shape;
  public currentRoi = null;
  public svgWidth: number = 1000;
  public svgHeight: number = 600;
  private _shapes = [];
  private _dragging: Shape;
  private _startPoint: any;
  private _svg: any;
  private _svgElement;
  private _polygon: Shape;

  private readonly _playerState$ = new Subject<SnPlayerState>();
  private readonly _destroy$ = new Subject<void>();
  private _currentPlayerState: SnPlayerState = SnPlayerState.Pending;

  get playerState$(): Observable<SnPlayerState> {
    return this._playerState$.asObservable();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SnCamera,
    private readonly _dialogRef: MatDialogRef<CameraAiGridDialogComponent>,
    private readonly _elementRef: ElementRef,
    private readonly _cd: ChangeDetectorRef,
    private readonly _dialogService: SnDialogService,
  ) {}

  public ngAfterViewInit(): void {
    this._svg = d3.select('#aiGridCanvas');
    this._svgElement = this._elementRef.nativeElement.querySelector('.coveringCanvas');
    this._playerState$.pipe(takeUntil(this._destroy$)).subscribe((state) => {
      this._currentPlayerState = state;
      if (this.currentRoi) {
        return;
      }

      if (state === SnPlayerState.Playing) {
        setTimeout(() => {
          this._genGridResolution();
          this.updateGridResolution();
          this._checkForPoints();
        }, 500);
      }
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._svg.remove();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    if (this._svg && this._currentPlayerState === SnPlayerState.Playing) {
      this.updateGridResolution();
    }
  }

  public updateGridResolution(): void {
    const svgBox = this._svgElement.getBBox();
    this.resolutionX = svgBox.width;
    this.resolutionY = svgBox.height;
    this.gridX = (svgBox.width - 1) / 160;
    this.gridY = (svgBox.height - 1) / 90;
    this._cd.detectChanges();
  }

  public onStateChange(state: SnPlayerState): void {
    this._playerState$.next(state);
  }

  public handleMouseMove(event: any): void {
    if (!this.drawing) {
      return;
    }
    if (event.target.id === 'gridRect' || event.target.hasAttribute('is-handle')) {
      const g = this._svg.select('#' + this.drawing.id);
      g.select('line').remove();
      g.insert('line', ':first-child')
        .attr('x1', this._startPoint[0])
        .attr('y1', this._startPoint[1])
        .attr('x2', event.offsetX)
        .attr('y2', event.offsetY)
        .attr('stroke', BASE_COLORS['accent-400'])
        .attr('stroke-width', 2);

      const x = event.offsetX;
      const y = event.offsetY;
      const gridX = this._round(Math.max(5, Math.min(this.resolutionX - 5, x)), this.gridX);
      const gridY = this._round(Math.max(5, Math.min(this.resolutionY - 5, y)), this.gridY);
      this.drawing.points.splice(this.drawing.points.length - 1, 1, [gridX, gridY]);
      this._updateShape(this.drawing);
    }
  }

  public handleMouseUp(event: any): void {
    if (this._dragging || this.isPolygonComplete) {
      return;
    }
    if (event.target.hasAttribute('is-handle')) {
      this._closePolygon(this.drawing);
      return;
    }
    const x = event.offsetX;
    const y = event.offsetY;
    const gridX = this._round(Math.max(5, Math.min(this.resolutionX - 5, x)), this.gridX);
    const gridY = this._round(Math.max(5, Math.min(this.resolutionY - 5, y)), this.gridY);
    this._startPoint = [gridX, gridY];

    if (!this.drawing) {
      const shape = new Shape();
      shape.id = `shape_${uuid.v4()}`;
      this._shapes.push(shape);
      this.drawing = shape;
      this._svg.append('g').attr('id', shape.id);
      this.drawing.points.push(this._startPoint);
    }

    const g = this._svg.select('#' + this.drawing.id);
    this.drawing.points.push(this._startPoint);
    g.select('polyline').remove();
    g.append('polyline')
      .attr('points', this.drawing.points)
      .style('fill', 'none')
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2);
    this._updateShape(this.drawing);
  }

  public reset(): void {
    const polygon = this._svg.selectAll('g');
    polygon.remove();
    this._dragging = null;
    this.drawing = null;
    this.isPolygonComplete = false;
  }

  public submit(): void {
    this.isLoading = true;
    const gridPoints = [];
    this._polygon.points.forEach((arry) => {
      gridPoints.push([Math.floor(arry[0] / this.gridX), Math.floor(arry[1] / this.gridY)]);
    });
    this._dialogRef.close([gridPoints]);
  }

  public deleteRegion(): void {
    this._dialogService
      .confirm({
        message: 'Are you sure you want to delete this region of interest?',
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._dialogRef.close([]);
      });
  }

  public cancel(): void {
    this._dialogRef.close();
  }

  private _genGridResolution(): void {
    const element = this._elementRef.nativeElement;
    const video = element.querySelector('video');
    const videoRatio = video.videoHeight / video.videoWidth;
    const clientRatio = video.clientHeight / video.clientWidth;
    if (videoRatio > clientRatio) {
      const ratio = video.clientHeight / video.videoHeight;
      const newVideoWidth = video.videoWidth * ratio;
      this.svgWidth = newVideoWidth;
      this.svgHeight = video.clientHeight;
      this._svgElement.style.left = `calc(50% - ${newVideoWidth / 2}px)`;
      this._svgElement.style.top = '0px';
      this._cd.detectChanges();
    } else {
      const ratio = video.clientWidth / video.videoWidth;
      const newVideoHeight = video.videoHeight * ratio;
      if (!isNaN(newVideoHeight)) {
        this.svgHeight = newVideoHeight;
        this.svgWidth = video.clientWidth;
        this._svgElement.style.top = `calc(50% - ${newVideoHeight / 2}px)`;
        this._svgElement.style.left = '0px';
        this._cd.detectChanges();
      }
    }
  }

  private _round(p: number, n: number): any {
    return p % n < n / 2 ? p - (p % n) : p + n - (p % n);
  }

  private _handleDrag(self: any): any {
    return function (d: any): void {
      const dragCircle = d3.select(this);
      const newPoints = [];
      let circle: d3.Selection<any, unknown, null, undefined>;
      const x = d3.event.x;
      const y = d3.event.y;
      const gridX = self._round(Math.max(5, Math.min(self.resolutionX - 5, x)), self.gridX);
      const gridY = self._round(Math.max(5, Math.min(self.resolutionY - 5, y)), self.gridY);

      const poly: any = d3.select(this.parentNode).select('polygon');
      const circles: any = d3.select(this.parentNode).selectAll('circle');
      dragCircle.attr('cx', gridX).attr('cy', gridY);
      for (let i = 0; i < circles._groups[0].length; i++) {
        circle = d3.select(circles._groups[0][i]);
        newPoints.push([Number(circle.attr('cx')), Number(circle.attr('cy'))]);
      }
      poly.attr('points', newPoints);

      self._dragging = self._shapes.find((s: { id: any }) => s.id === this.parentNode.id);
      self._dragging.points = newPoints;
    };
  }

  private _endDrag(self: any): any {
    return (d: any) => {
      self._dragging = null;
    };
  }

  private _updateShape(shape: Shape, closed: boolean = false): void {
    this._updatePoints(shape, closed);
  }

  private _updatePoints(shape: Shape, closed: boolean = false): void {
    const g = this._svg.select('#' + shape.id);
    g.selectAll('circle').remove();
    let pointLength = shape.points.length - 1;
    if (closed) {
      pointLength++;
    }
    for (let i = 0; i < pointLength; i++) {
      const point = shape.points[i];
      const circle = g
        .append('circle')
        .attr('cx', point[0])
        .attr('cy', point[1])
        .attr('r', 5)
        .attr('fill', BASE_COLORS['accent-400'])
        .attr('fill-opacity', '0.9')
        .attr('stroke', '#000')
        .attr('stroke-width', '2');
      if (closed) {
        const dragger = d3.drag().on('drag', this._handleDrag(this)).on('end', this._endDrag(this));
        circle.call(dragger).style('cursor', 'move');
      } else {
        if (i === 0) {
          circle.attr('is-handle', 'true').style('cursor', 'pointer');
        }
      }
    }
  }

  private _closePolygon(shape: Shape): void {
    const g = this._svg.select('#' + shape.id);
    g.insert('polygon', ':first-child')
      .attr('points', shape.points)
      .style('fill-opacity', '0.3')
      .style('fill', BASE_COLORS['accent-400'])
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2);

    g.select('polyline').remove();
    g.select('line').remove();
    this.drawing.points.splice(0, 1);
    this._updateShape(shape, true);
    this._polygon = this.drawing;
    this.drawing = null;
    this.isPolygonComplete = true;
  }

  private _checkForPoints(): void {
    this.currentRoi = this.data.aiDetectionTypes[0].roi[0];
    const shape = new Shape();
    const gridPoints = [];
    this._startPoint = this.currentRoi[0];
    this.currentRoi.forEach((arry) => {
      gridPoints.push([arry[0] * this.gridX, arry[1] * this.gridY]);
    });
    this._startPoint = gridPoints[0];
    shape.id = `shape_${uuid.v4()}`;
    shape.points = gridPoints;
    this._shapes.push(shape);
    this._svg.append('g').attr('id', shape.id);
    this.drawing = shape;
    const g = this._svg.select('#' + this.drawing.id);
    this.drawing.points.push(this._startPoint);
    g.select('polyline').remove();
    g.append('polyline')
      .attr('points', this.drawing.points)
      .style('fill', 'none')
      .attr('stroke', BASE_COLORS['accent-400'])
      .attr('stroke-width', 2);
    this._updateShape(this.drawing);
    this._closePolygon(this.drawing);
  }
}
